'use client';

import React, { useRef } from 'react';
import { useTranslations } from 'next-intl';
import AppImage from '@/components/ui/AppImage';
import SectionLabel from '@/components/ui/SectionLabel';
import { delay, useReveal } from '@/hooks/useReveal';
import { richTags } from '@/i18n/rich';

const IMG = {
  product: '/assets/images/nomafe-como-funciona-producto.webp',
  box: '/assets/images/nomafe-que-incluye.webp',
};

/** Double click on the button — two taps inside a ring. */
function DoubleClickGlyph() {
  return (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1" opacity="0.55" />
      <circle cx="17" cy="24" r="3.2" fill="currentColor" />
      <circle cx="31" cy="24" r="3.2" fill="currentColor" />
    </svg>
  );
}

/** Long press on the button — a ring that fills while it is held. */
function LongPressGlyph() {
  return (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      <circle
        cx="24"
        cy="24"
        r="22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="104 138"
        transform="rotate(-90 24 24)"
      />
      <circle cx="24" cy="24" r="3.2" fill="currentColor" />
    </svg>
  );
}

/**
 * Instructions page (/funcionamiento) — short, scannable. Three blocks, most of the content is real text:
 *  1 · title + the six steps, with one clean product photo
 *  2 · "Un botón, dos modos" (double click / long press)
 *  3 · "Qué incluye" — the box photo (the only infographic) next to the nine items
 * On phones every image sits above its text.
 */
export default function HowItWorksSection() {
  const t = useTranslations('howItWorks');
  const steps = t.raw('steps') as { title: string; text: string }[];
  const modes = t.raw('modes.items') as { tag: string; title: string; text?: string; when: string }[];
  const includes = t.raw('includes.items') as string[];
  const sectionRef = useRef<HTMLElement>(null);
  useReveal(sectionRef);

  return (
    <section ref={sectionRef} id="asi-funciona" className="relative py-28 lg:py-40">
      <div className="max-w-[146rem] mx-auto px-7 sm:px-8 lg:px-12">
        {/* 1 · Title, the six steps and the whole product */}
        <div className="grid lg:grid-cols-[1fr_auto] gap-x-[clamp(40px,6vw,110px)] gap-y-12 items-center">
          <div className="lg:col-start-1 lg:row-start-1">
            <SectionLabel className="mb-7">{t('label')}</SectionLabel>
            <h1 className="display display-lg" data-reveal="" style={delay(100)}>
              {t.rich('title', richTags)}
            </h1>
            <p className="body-copy mt-6 max-w-[440px]" data-reveal="" style={delay(200)}>
              {t('intro')}
            </p>

            <ol className="mt-9 max-w-[580px] border-b border-[var(--line)]">
              {steps.map((step, i) => (
                <li
                  key={step.title}
                  className="border-t border-[var(--line)]"
                  data-reveal=""
                  style={delay(120 + i * 70)}
                >
                  <div className="py-4 sm:flex sm:items-baseline sm:gap-5">
                    <div className="flex items-baseline gap-5 sm:w-[46%] shrink-0">
                      <span className="label-mono text-copper w-7 shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="display text-[clamp(22px,2vw,30px)] leading-[1.1]">
                        {step.title}
                      </span>
                    </div>
                    <p className="body-copy mt-1 pl-12 sm:mt-0 sm:pl-0">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mx-auto lg:mx-0 w-[min(60%,28vh)] lg:w-[min(250px,calc((100vh-290px)*0.6667))] lg:col-start-2 lg:row-start-1">
            <div className="relative aspect-[2/3] overflow-hidden" data-reveal="clip">
              <AppImage
                src={IMG.product}
                alt={t('productAlt')}
                fill
                sizes="(max-width: 1024px) 60vw, 250px"
                priority
                quality={85}
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>

        {/* 2 · One button, two modes */}
        <div className="mt-28 lg:mt-40 grid lg:grid-cols-[42fr_58fr] gap-x-[clamp(32px,5vw,96px)] gap-y-10 items-center">
          <div>
            <SectionLabel className="mb-7">{t('modes.label')}</SectionLabel>
            <h2 className="display display-md" data-reveal="" style={delay(100)}>
              {t.rich('modes.title', richTags)}
            </h2>
          </div>
          <div>
            <ul className="border-b border-[var(--line)]">
              {modes.map((mode, i) => (
                <li
                  key={mode.tag}
                  className="group border-t border-[var(--line)]"
                  data-reveal=""
                  style={delay(120 + i * 110)}
                >
                  <div className="flex items-start gap-6 lg:gap-8 py-8 lg:py-9 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-[4px]">
                    <span className="text-copper shrink-0 mt-1">
                      {i === 0 ? <DoubleClickGlyph /> : <LongPressGlyph />}
                    </span>
                    <div className="min-w-0">
                      <p className="label-mono text-copper">{mode.tag}</p>
                      <h3 className="display text-[clamp(30px,3.2vw,48px)] leading-[1.02] mt-3">
                        {mode.title}
                      </h3>
                      {mode.text && <p className="body-copy mt-3 max-w-[max(24rem,300px)]">{mode.text}</p>}
                      <p className="body-copy mt-3 max-w-[max(24rem,300px)] text-pretty text-[var(--cream)]">{mode.when}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 3 · What's in the box — one photo as visual support + the nine items as real text */}
        <div className="mt-28 lg:mt-40 grid lg:grid-cols-[minmax(0,440px)_minmax(0,460px)] lg:justify-center gap-x-[clamp(40px,6vw,110px)] gap-y-12 items-center">
          <figure className="w-full max-w-[min(100%,440px,calc((100vh-300px)*1.5))]">
            <div className="relative aspect-[3/2] overflow-hidden" data-reveal="clip">
              <AppImage
                src={IMG.box}
                alt={t('includes.alt')}
                fill
                sizes="(max-width: 1024px) 100vw, 440px"
                quality={85}
                style={{ objectFit: 'cover' }}
              />
            </div>
            <figcaption className="label-mono text-[var(--ash)] mt-4 flex justify-between gap-6">
              <span>{t('includes.fig')}</span>
              <span className="text-right">{t('includes.caption')}</span>
            </figcaption>
          </figure>
          <div>
            <SectionLabel className="mb-7">{t('includes.label')}</SectionLabel>
            <h2 className="display display-md" data-reveal="" style={delay(100)}>
              {t('includes.title')}
            </h2>
            <p className="body-copy mt-5" data-reveal="" style={delay(200)}>
              {t('includes.intro')}
            </p>
            <ol className="mt-7 max-w-[460px] border-b border-[var(--line)]">
              {includes.map((item, i) => (
                <li
                  key={item}
                  className="flex items-baseline gap-5 border-t border-[var(--line)] py-3"
                  data-reveal=""
                  style={delay(120 + i * 50)}
                >
                  <span className="label-mono text-copper w-7 shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="display text-[clamp(18px,1.6vw,24px)] leading-[1.15]">
                    {item}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
